<script>
export default {
  name: "TapCoinReactor",
  props: {
    active: { type: Boolean, default: false },
    tapCount: { type: Number, default: 0 },
    remainingMs: { type: Number, default: 10000 },
    durationMs: { type: Number, default: 10000 },
    countdown: { type: Number, default: 0 },
    submitting: { type: Boolean, default: false },
    waitingForRound: { type: Boolean, default: false }
  },
  computed: {
    timeLabel() {
      return Math.max(0, this.remainingMs / 1000).toFixed(1);
    },
    estimatedRewardLabel() {
      return (this.tapCount / 2).toFixed(1);
    },
    progressStyle() {
      const duration = Math.max(1, this.durationMs);
      const progress = Math.max(0, Math.min(1, this.remainingMs / duration));
      return { "--time-progress": `${progress * 360}deg` };
    },
    chargeStyle() {
      return { width: `${Math.min(100, this.tapCount * 2)}%` };
    },
    reactorClasses() {
      return {
        "is-active": this.active,
        "is-charged": this.tapCount >= 30,
        "is-overdrive": this.tapCount >= 50
      };
    },
    particleStyles() {
      return Array.from({ length: 10 }, (_, index) => ({
        "--particle-angle": `${index * 36}deg`,
        "--particle-distance": `${58 + (index % 3) * 18}px`,
        animationDelay: `${(index % 2) * 0.02}s`
      }));
    }
  },
  methods: {
    handlePointerDown(event) {
      if (!this.active || event.isPrimary !== true) return;
      event.preventDefault();
      this.$emit("tap", event);
    }
  }
};
</script>

<template>
  <section class="reactor-card" :class="reactorClasses">
    <div class="reactor-card__topbar">
      <div class="reactor-stat">
        <span class="reactor-stat__label">เหรียญที่จะได้รับ</span>
        <strong class="reactor-stat__value reactor-stat__value--coin"
          >+{{ estimatedRewardLabel }}</strong
        >
      </div>
      <div class="reactor-stat reactor-stat--right">
        <span class="reactor-stat__label">เวลา</span>
        <strong class="reactor-stat__value">{{ timeLabel }}</strong>
      </div>
    </div>

    <div class="reactor-stage">
      <div class="reactor-grid" aria-hidden="true"></div>
      <div class="reactor-orbit reactor-orbit--one" aria-hidden="true"></div>
      <div class="reactor-orbit reactor-orbit--two" aria-hidden="true"></div>

      <div class="reactor-timer" :style="progressStyle">
        <button
          type="button"
          class="reactor-core"
          :disabled="!active"
          aria-label="แตะรัวเพื่อรับเหรียญ"
          @pointerdown="handlePointerDown"
        >
          <span class="reactor-core__glow" aria-hidden="true"></span>
          <span class="reactor-core__coin">฿</span>
          <span class="reactor-core__copy">{{
            active ? "TAP!" : "READY"
          }}</span>
        </button>
      </div>

      <div :key="`burst-${tapCount}`" class="tap-burst" aria-hidden="true">
        <span
          v-for="(style, index) in particleStyles"
          :key="index"
          class="tap-particle"
          :style="style"
        ></span>
      </div>

      <span
        v-if="active && tapCount > 0"
        :key="`gain-${tapCount}`"
        class="tap-gain"
        aria-hidden="true"
      >
        +0.5
      </span>

      <div v-if="countdown > 0" class="reactor-countdown" aria-live="assertive">
        <span>{{ countdown }}</span>
      </div>
      <div
        v-if="waitingForRound"
        class="reactor-countdown reactor-countdown--submit"
        aria-live="polite"
      >
        <span class="reactor-loader"></span>
        <small>กำลังยืนยันสิทธิ์</small>
      </div>
      <div
        v-if="submitting"
        class="reactor-countdown reactor-countdown--submit"
        aria-live="polite"
      >
        <span class="reactor-loader"></span>
        <small>กำลังบันทึกเหรียญ</small>
      </div>
    </div>

    <div class="charge-meter" aria-hidden="true">
      <div class="charge-meter__track">
        <span class="charge-meter__fill" :style="chargeStyle"></span>
      </div>
      <span class="charge-meter__label">
        {{ tapCount }} TAP × 0.5 =
        <strong>{{ estimatedRewardLabel }} เหรียญ</strong>
      </span>
    </div>

    <p class="reactor-hint">
      {{
        active
          ? "ทุก TAP เพิ่ม 0.5 เหรียญ • จบรอบปัดเศษขึ้น"
          : "กดเริ่มเกม แล้วเตรียมแตะรัวเป็นเวลา 10 วินาที"
      }}
    </p>
  </section>
</template>

<style scoped>
.reactor-card {
  position: relative;
  width: min(100%, 420px);
  padding: 18px 18px 20px;
  overflow: hidden;
  border: 1px solid rgba(255, 184, 71, 0.28);
  border-radius: 28px;
  background: linear-gradient(
      165deg,
      rgba(31, 13, 56, 0.98),
      rgba(8, 7, 27, 0.98)
    ),
    #09071d;
  box-shadow: 0 24px 80px rgba(4, 0, 20, 0.58),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  transition: border-color 0.25s ease, box-shadow 0.25s ease;
}

.reactor-card.is-active {
  border-color: rgba(71, 226, 255, 0.58);
  box-shadow: 0 26px 90px rgba(7, 0, 31, 0.72),
    0 0 36px rgba(36, 205, 255, 0.16);
}

.reactor-card__topbar {
  position: relative;
  z-index: 3;
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.reactor-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.reactor-stat--right {
  text-align: right;
}
.reactor-stat__label {
  color: #9990b1;
  font-size: 11px;
  letter-spacing: 0.08em;
}
.reactor-stat__value {
  color: #fff;
  font-size: 28px;
  line-height: 1;
  font-weight: 900;
}
.reactor-stat__value--coin {
  color: #ffc95c;
  text-shadow: 0 0 20px rgba(255, 175, 55, 0.38);
}

.reactor-stage {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 296px;
  margin-top: 4px;
  isolation: isolate;
}

.reactor-grid {
  position: absolute;
  inset: 14px -18px 0;
  opacity: 0.28;
  background-image: linear-gradient(
      rgba(100, 210, 255, 0.1) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(100, 210, 255, 0.1) 1px, transparent 1px);
  background-size: 28px 28px;
  transform: perspective(320px) rotateX(58deg) scale(1.28);
  mask-image: linear-gradient(to bottom, transparent, #000 30%, #000);
}

.reactor-orbit {
  position: absolute;
  width: 224px;
  height: 224px;
  border: 1px solid rgba(94, 217, 255, 0.28);
  border-radius: 50%;
  pointer-events: none;
}

.reactor-orbit::before,
.reactor-orbit::after {
  position: absolute;
  width: 10px;
  height: 10px;
  content: "";
  border-radius: 50%;
  background: #62e4ff;
  box-shadow: 0 0 15px #36d6ff;
}

.reactor-orbit::before {
  top: 22px;
  left: 25px;
}
.reactor-orbit::after {
  right: 20px;
  bottom: 32px;
  background: #ffbc42;
  box-shadow: 0 0 15px #ff9d2e;
}
.reactor-orbit--one {
  animation: orbitSpin 7s linear infinite;
}
.reactor-orbit--two {
  width: 264px;
  height: 264px;
  border-style: dashed;
  animation: orbitSpin 11s linear infinite reverse;
}

.reactor-timer {
  --time-progress: 360deg;
  position: relative;
  z-index: 2;
  display: grid;
  width: 190px;
  height: 190px;
  place-items: center;
  border-radius: 50%;
  background: conic-gradient(
    #63e6ff var(--time-progress),
    rgba(255, 255, 255, 0.08) 0
  );
  box-shadow: 0 0 34px rgba(44, 209, 255, 0.18);
}

.reactor-timer::before {
  position: absolute;
  inset: 7px;
  content: "";
  border-radius: inherit;
  background: #100b2a;
}

.reactor-core {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 156px;
  height: 156px;
  padding: 0;
  overflow: hidden;
  color: #160a2b;
  border: 2px solid rgba(255, 247, 208, 0.9);
  border-radius: 50%;
  outline: none;
  background: radial-gradient(
    circle at 38% 30%,
    #fff9c9 0,
    #ffd259 28%,
    #ff8d2b 62%,
    #e8445f 100%
  );
  box-shadow: 0 0 0 9px rgba(255, 160, 42, 0.1),
    0 0 44px rgba(255, 137, 38, 0.52), inset 0 -12px 30px rgba(111, 16, 77, 0.3);
  touch-action: manipulation;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition: filter 0.2s ease, transform 0.08s ease;
}

.reactor-core:not(:disabled) {
  cursor: pointer;
}
.reactor-core:not(:disabled):active {
  transform: scale(0.91);
  filter: brightness(1.22);
}
.reactor-core:disabled {
  opacity: 0.68;
}
.reactor-core__glow {
  position: absolute;
  inset: 22px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.24);
  filter: blur(16px);
}
.reactor-core__coin {
  position: relative;
  font-size: 52px;
  line-height: 0.95;
  font-weight: 1000;
  text-shadow: 0 2px 0 rgba(255, 255, 255, 0.32);
}
.reactor-core__copy {
  position: relative;
  margin-top: 7px;
  font-size: 14px;
  font-weight: 1000;
  letter-spacing: 0.18em;
}

.is-active .reactor-core {
  animation: coreBreathe 0.7s ease-in-out infinite alternate;
}
.is-charged .reactor-core {
  box-shadow: 0 0 0 11px rgba(255, 160, 42, 0.12),
    0 0 58px rgba(255, 137, 38, 0.72), inset 0 -12px 30px rgba(111, 16, 77, 0.3);
}
.is-overdrive .reactor-core {
  filter: saturate(1.18) brightness(1.08);
}

.tap-burst {
  position: absolute;
  z-index: 3;
  width: 1px;
  height: 1px;
  pointer-events: none;
}
.tap-particle {
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ffe57b;
  box-shadow: 0 0 12px #ffae32;
  opacity: 0;
  transform: rotate(var(--particle-angle)) translateX(44px) scale(0.4);
}

.is-active .tap-particle {
  animation: particleBurst 0.42s ease-out;
}

.tap-gain {
  position: absolute;
  z-index: 5;
  top: 58px;
  left: 50%;
  padding: 5px 12px 6px;
  color: #fff1a7;
  border: 1px solid rgba(255, 220, 119, 0.58);
  border-radius: 999px;
  background: rgba(18, 7, 42, 0.9);
  font-size: 30px;
  line-height: 1;
  font-weight: 1000;
  pointer-events: none;
  text-shadow: 0 0 18px rgba(255, 176, 41, 0.9), 0 2px 0 rgba(89, 32, 5, 0.7);
  transform: translateX(-50%);
  animation: tapGain 0.48s ease-out both;
}

.reactor-countdown {
  position: absolute;
  z-index: 6;
  display: grid;
  inset: 0;
  place-items: center;
  border-radius: 22px;
  background: rgba(8, 5, 27, 0.64);
  backdrop-filter: blur(4px);
}

.reactor-countdown > span {
  color: #fff2ad;
  font-size: 112px;
  line-height: 1;
  font-weight: 1000;
  text-shadow: 0 0 36px rgba(255, 180, 50, 0.62);
  animation: countdownPop 0.5s ease both;
}
.reactor-countdown--submit {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 14px;
}
.reactor-countdown--submit small {
  color: #eee8ff;
  font-size: 14px;
}
.reactor-loader {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.16);
  border-top-color: #ffd05b;
  border-radius: 50%;
  animation: orbitSpin 0.8s linear infinite;
}

.charge-meter {
  position: relative;
  z-index: 3;
}
.charge-meter__track {
  height: 9px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
}
.charge-meter__fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #4fddff, #9c63ff 48%, #ffb33c);
  box-shadow: 0 0 18px rgba(92, 219, 255, 0.7);
  transition: width 0.12s ease;
}
.charge-meter__label {
  display: block;
  margin-top: 8px;
  color: #a69db9;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
  letter-spacing: 0.02em;
}
.charge-meter__label strong {
  color: #ffd36c;
  font-size: 14px;
  font-weight: 1000;
}
.reactor-hint {
  position: relative;
  z-index: 3;
  min-height: 38px;
  margin: 14px 8px 0;
  color: #bdb5cf;
  font-size: 13px;
  line-height: 1.45;
  text-align: center;
}

@keyframes orbitSpin {
  to {
    transform: rotate(360deg);
  }
}
@keyframes coreBreathe {
  from {
    filter: brightness(0.98);
  }
  to {
    filter: brightness(1.16);
  }
}
@keyframes countdownPop {
  from {
    opacity: 0;
    transform: scale(1.45);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
@keyframes particleBurst {
  0% {
    opacity: 1;
    transform: rotate(var(--particle-angle)) translateX(42px) scale(0.5);
  }
  100% {
    opacity: 0;
    transform: rotate(var(--particle-angle))
      translateX(var(--particle-distance)) scale(1.15);
  }
}
@keyframes tapGain {
  0% {
    opacity: 1;
    transform: translate(-50%, 18px) scale(0.72);
  }
  32% {
    opacity: 1;
    transform: translate(-50%, 0) scale(1.12);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -34px) scale(0.96);
  }
}

@media (max-height: 700px) {
  .reactor-stage {
    height: 250px;
  }
  .reactor-orbit--two {
    width: 230px;
    height: 230px;
  }
  .reactor-card {
    padding-top: 14px;
    padding-bottom: 14px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .reactor-orbit,
  .reactor-core,
  .tap-particle,
  .tap-gain,
  .reactor-countdown > span {
    animation: none !important;
  }
}
</style>
